/**
 * useStaggeredReveal: caps a list to an initial slice and reveals the rest
 * one-by-one on a steady cadence when the user opts in.
 *
 * Mirrors the "Show more" reveal in the CreateInterviewForm objectives step
 * (see {@link components/hooks/useSuggestionReveal}): the first `initialVisible`
 * items render immediately, and extras drop in progressively so the expansion
 * reads as a build rather than a blunt jump. Generic over plain counts so any
 * list (report actions, open questions, etc.) can use it.
 *
 * @module components/hooks/useStaggeredReveal
 */

import { useState, useRef, useEffect, useCallback } from 'react';

/** Default min cadence between extra-item reveals. */
const DEFAULT_REVEAL_CADENCE_MS = 90;

/**
 * Result of the useStaggeredReveal hook.
 */
export interface UseStaggeredRevealResult {
  /** How many items should currently render (slice the list to this). */
  visibleCount: number;
  /** Whether the "Show more" affordance should be offered. */
  canShowMore: boolean;
  /** How many items are still hidden behind "Show more" (for the label). */
  hiddenCount: number;
  /** Reveal the hidden items one-by-one on the cadence. */
  showMore: () => void;
}

/**
 * Cap a list at `initialVisible` items with a staggered "Show more" reveal.
 *
 * @param total - Total number of items in the list.
 * @param initialVisible - How many to show before "Show more" is used.
 * @param cadenceMs - Delay between successive extra reveals.
 * @returns Reveal state and the showMore callback.
 */
export function useStaggeredReveal(
  total: number,
  initialVisible: number,
  cadenceMs: number = DEFAULT_REVEAL_CADENCE_MS,
): UseStaggeredRevealResult {
  const cappedInitial = Math.min(initialVisible, total);
  const [visibleCount, setVisibleCount] = useState(cappedInitial);
  // Latches true the moment the user clicks "Show more" so the affordance
  // disappears immediately, while the extras stagger in behind it.
  const [expanded, setExpanded] = useState(false);

  /** Pending reveal timers, cancelled on unmount to avoid post-unmount updates. */
  const timersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  // Keep the floor in sync if the list grows/shrinks (e.g. async data). Never
  // drops below what the user has already revealed, and never exceeds total.
  useEffect(() => {
    setVisibleCount((prev) => Math.min(Math.max(prev, cappedInitial), total));
  }, [cappedInitial, total]);

  const showMore = useCallback(() => {
    setExpanded(true);
    const extras = Math.max(0, total - cappedInitial);
    for (let i = 1; i <= extras; i++) {
      const target = cappedInitial + i;
      const timer = setTimeout(() => {
        timersRef.current.delete(timer);
        setVisibleCount((prev) => Math.max(prev, target));
      }, i * cadenceMs);
      timersRef.current.add(timer);
    }
  }, [total, cappedInitial, cadenceMs]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const t of timers) clearTimeout(t);
      timers.clear();
    };
  }, []);

  return {
    visibleCount,
    canShowMore: !expanded && total > cappedInitial,
    hiddenCount: Math.max(0, total - cappedInitial),
    showMore,
  };
}
